/**
 * A/B tests on the host page: which variant of each running test this visitor
 * sees, kept stable across pages and visits, and the overrides that variant
 * makes to the site's normal settings.
 *
 * The assignment lives HERE, in the host page, because the preload is the same
 * for every visitor and publicly cached. The rules (stored draw first, hash of
 * a stable seed second, `?bh_ab=` forcing) are the shared contract in
 * @bluehat/shared/experiments/experiments; this file is only the storage and
 * the narrowing of wire values.
 *
 * Zod-free: bundled into bh.js.
 */
import {
  AB_FORCE_PARAM,
  AB_STORAGE_KEY,
  assignVariant,
  isExperimentSurface,
  liveExperiments,
  parseAbAssignments,
  type AbAssignments,
  type DeliveredExperiment,
  type ExperimentSurface,
} from "@bluehat/shared/experiments/experiments";
import { isLivingBarSkin, type LivingBarSkin } from "@bluehat/shared/widget-design/living-bar";
import { isBarContent, isInlineMode, type BarContent, type InlineMode } from "@bluehat/shared/widget-design/inline";

/** The variant one visitor sees in one running test. */
export type AbPick = {
  experimentId: string;
  surface: ExperimentSurface;
  variant: string;
  /** Forced with `?bh_ab=`: shown, never reported. */
  forced: boolean;
};

/** What the visitor's variants change, narrowed to values this bundle knows. */
export type AbOverrides = {
  skin?: LivingBarSkin;
  barContent?: BarContent;
  inlineMode?: InlineMode;
};

export type AbState = {
  picks: { [S in ExperimentSurface]?: AbPick };
  overrides: AbOverrides;
};

export const NO_AB: AbState = { picks: {}, overrides: {} };

/** Minimal storage surface, so tests pass a fake and a blocked one. */
export type AbStorage = { getItem(k: string): string | null; setItem(k: string, v: string): void };

function randomSeed(): string {
  try {
    const b = crypto.getRandomValues(new Uint8Array(12));
    return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  }
}

function readDoc(storage: AbStorage | null): AbAssignments | null {
  try {
    return parseAbAssignments(storage?.getItem(AB_STORAGE_KEY) ?? null);
  } catch {
    return null;
  }
}

function writeDoc(storage: AbStorage | null, doc: AbAssignments): void {
  try {
    storage?.setItem(AB_STORAGE_KEY, JSON.stringify(doc));
  } catch {
    /* storage blocked: the seed fallback keeps the draw stable for the session */
  }
}

/**
 * Resolve every running test for this visitor.
 *
 * `fallbackSeed` (the session id, when there is one) is used only when storage
 * holds no seed and refuses to keep one — a browser that blocks storage then
 * still sees the same variant on every page of the session.
 *
 * One test per surface: if a (stale) payload ever carried two, the first wins.
 */
export function resolveAbTests(
  experiments: readonly DeliveredExperiment[] | undefined,
  opts: { now?: number; storage: AbStorage | null; search: string; fallbackSeed: string | null },
): AbState {
  // The clock only drops tests a stale cached payload says have ended; it is
  // read here, not in the config handler, which must stay free of clock reads
  // (the kill switch is reconciled in the iframe — scope-chokepoint.test.ts).
  const live = liveExperiments(experiments ?? [], opts.now ?? Date.now()).filter((e) => isExperimentSurface(e.surface));
  if (live.length === 0) return NO_AB;

  let forcedKey: string | null = null;
  try {
    forcedKey = new URLSearchParams(opts.search).get(AB_FORCE_PARAM);
  } catch {
    forcedKey = null;
  }

  const stored = readDoc(opts.storage);
  const doc: AbAssignments = stored ?? { seed: randomSeed(), assigned: {} };
  let changed = stored == null;
  if (stored == null) {
    // Did the new seed stick? If not, prefer the session id: stable for the
    // session, where a fresh random seed would redraw on every page.
    writeDoc(opts.storage, doc);
    if (readDoc(opts.storage) == null && opts.fallbackSeed) {
      doc.seed = opts.fallbackSeed;
      changed = false;
    }
  }

  const state: AbState = { picks: {}, overrides: {} };
  for (const e of live) {
    const surface = e.surface;
    if (!isExperimentSurface(surface) || state.picks[surface]) continue;
    const r = assignVariant(e, doc, forcedKey);
    if (!r.variant) continue;
    changed = changed || r.changed;
    state.picks[surface] = { experimentId: e.id, surface, variant: r.variant.key, forced: r.forced };
    if (surface === "lower_bar") {
      if (isLivingBarSkin(r.variant.skin)) state.overrides.skin = r.variant.skin;
      if (isBarContent(r.variant.barContent)) state.overrides.barContent = r.variant.barContent;
    } else {
      if (isInlineMode(r.variant.inlineMode)) state.overrides.inlineMode = r.variant.inlineMode;
    }
  }
  if (changed) writeDoc(opts.storage, doc);
  return state;
}

/**
 * Report each pick once per page (path + search), and never a forced one. The
 * results count a session once, in the first variant it saw, so repeats cost a
 * row and change nothing; this keeps them to one per page.
 */
export function createExposureReporter(send: (pick: AbPick, url: string) => void) {
  const sent = new Set<string>();
  return (pick: AbPick | undefined, url: string): void => {
    if (!pick || pick.forced) return;
    const key = `${pick.experimentId}|${pick.variant}|${url}`;
    if (sent.has(key)) return;
    sent.add(key);
    send(pick, url);
  };
}
